import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import React, { useState } from 'react';

interface Props {
  busy: boolean;
  upload: (title: string, file: File) => Promise<void> | void;
}

const MAX_BYTES = 20_000_000;

/** Adds a PDF to the offer's documents under a title the investor sees. */
export default function OfferDocumentUpload({ busy, upload }: Props) {
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [problem, setProblem] = useState('');

  const choose = (chosen: File | undefined) => {
    setProblem('');
    if (chosen && chosen.size > MAX_BYTES) {
      setFile(null);
      setProblem('Choose a PDF no larger than 20 MB');
      return;
    }
    setFile(chosen ?? null);
    if (chosen && !title) setTitle(chosen.name.replace(/\.pdf$/i, ''));
  };

  const submit = async () => {
    if (!file || !title.trim()) return;
    await upload(title.trim(), file);
    setTitle('');
    setFile(null);
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        Upload a PDF such as the information memorandum or a valuation. It is added to the documents
        list above.
      </Typography>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
        <TextField
          label="Document title"
          size="small"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          inputProps={{ maxLength: 120 }}
        />
        <Button component="label" variant="outlined" disabled={busy}>
          {file ? file.name : 'Choose PDF'}
          <input
            hidden
            type="file"
            accept="application/pdf"
            aria-label="Offer document PDF"
            onChange={(event) => choose(event.target.files?.[0])}
          />
        </Button>
      </Box>
      {problem && (
        <Typography variant="caption" color="error">
          {problem}
        </Typography>
      )}
      <Box>
        <Button variant="contained" disabled={busy || !file || !title.trim()} onClick={submit}>
          Upload document
        </Button>
      </Box>
    </Stack>
  );
}
